import React, { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Skeleton,
  useTheme,
  Typography,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import CveChart from "./CveChart";
import DrawerWrapper from "../../../components/drawer/Drawer";
import AgGridTable from "../../../components/table/AgGridTable";
import { ICellRendererParams } from "ag-grid-community";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import InfoIcon from "@mui/icons-material/Info";
import { CVEData } from "../../../pages/onPremises/cve/CVEScreen";
import { useNavigate } from "react-router-dom";
import eventEmitter from "../../../components/utils/eventEmitter";
import LaunchSharpIcon from "@mui/icons-material/LaunchSharp";
import CVEDetailsDrawer from "../../../components/cve/CVEDetailsDrawer";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useDashboardContext } from "../DashboardContext";

interface VulnerabilityData {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface APIResponse {
  status: number;
  message: string;
  data: {
    num_of_pages: number;
    cve_list: CVEData[];
  };
}

const Cve: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [vulnerabilityData, setVulnerabilityData] = useState<VulnerabilityData>(
    {
      total: 0,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    }
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]);
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [trigger, setTrigger] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCVE, setSelectedCVE] = useState<any>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const isOnlyCloudUser = useSelector((state: RootState) => state.UserLoginDetails.isOnlyCloudUser);
  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(isDrawerOpen || drawerOpen);
  }, [isDrawerOpen, drawerOpen, setIsOverlayOpen]);

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  const fetchCVEData = async (severity: string) => {
    setIsLoading(true);
    try {
      const payload = { type: 1 };
      const response = await axiosInstance.post<APIResponse>(
        "securityscan/cve/search_cve",
        payload
      );
      if (response.data.status === 200) {
        return response.data.data.cve_list;
      }
      return [];
    } catch (error) {
      console.error("Error fetching CVE data:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleChartClick = async (severity: string) => {
    setIsDrawerOpen(true);
    setSelectedSeverity(severity);
    const apiData = await fetchCVEData(severity);
    const filtered = apiData.filter(
      (item: any) => item.severity?.toLowerCase() === severity.toLowerCase()
    );
    setTableData(filtered);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "high":
        return "#DC2626";
      case "medium":
        return "#F97316";
      case "low":
        return "#F2BF00";
      case "critical":
        return "#670004";
      default:
        return "#6B7280";
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "high":
        return "rgba(220, 38, 38, 0.1)";
      case "medium":
        return "rgba(249, 115, 22, 0.1)";
      case "low":
        // Low: { bgcolor: "#F2BF0024", color: "#F2BF00" },

        return "#F2BF0024";
      case "critical":
        return "#67000424";
      default:
        return "rgba(107, 114, 128, 0.1)";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_CVE_VULNERABILITIES ('securityscan/dashboard-cve-vulnerabilities').
        // Backend removed 'dashboard-cve-vulnerabilities' API and consolidated the logic into 'dashboard-overall-vulnerabilities' (END_POINTS.DASHBOARD_MISCONFIGURATION).
        // It is now called using POST with payload { module: "dashboard-cve-vulnerabilities" }.
        const response = await axiosInstance.post(
          END_POINTS.DASHBOARD_MISCONFIGURATION,
          {
            module: "dashboard-cve-vulnerabilities",
          }
        );

        if (response.data && response.data.data) {
          setVulnerabilityData({
            total: response.data.data.count || 0,
            critical: response.data.data.critical || 0,
            high: response.data.data.high || 0,
            medium: response.data.data.medium || 0,
            low: response.data.data.low || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching CVE data:", error);
        setVulnerabilityData({
          total: 0,
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trigger]);

  const onRowClick = async (cveData: CVEData) => {
    setDrawerOpen(true);
    setSelectedRow(cveData);
    setSelectedCVE(cveData);
    setIsLoading(true)

    try {
      const payload = { cve: cveData.cve_id };
      const response = await axiosInstance.post(
        "securityscan/cve/cve_summary",
        payload
      );

      if (response.data.status === 200) {
        setSelectedCVE({
          ...cveData,
          summaryData: response.data.data.cve_summary_details,
        });
      }

    } catch (error) {
      console.error("Error fetching CVE summary:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };


  

if (!isOnlyCloudUser && loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: "4px",
          height: 420,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // boxShadow:
          //   theme.palette.mode === "dark"
          //     ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
          //     : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 1,
            px: 2,
          }}
        >
          <Skeleton variant="text" width={150} height={32} />
        </Box>

        {/* Chart area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
            height: "250px",
          }}
        >
          {/* CVE Score Area Skeleton */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Skeleton variant="circular" width={120} height={120} />
          </Box>

          {/* Vulnerabilities Skeleton */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-around",
              px: 2,
              mb: 3,
            }}
          >
            {[...Array(4)].map((_, index) => (
              <Box key={index} sx={{ textAlign: "center" }}>
                <Skeleton variant="text" width={40} height={40} />
                <Skeleton
                  variant="text"
                  width={60}
                  height={20}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            ))}
          </Box>

          {/* Cyber Resilience Skeleton */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            {[...Array(3)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: "center",
                  flex: 1,
                  borderRight:
                    index < 2 ? `1px solid ${theme.palette.divider}` : "none",
                }}
              >
                <Skeleton variant="text" width={80} height={24} />
                <Skeleton
                  variant="text"
                  width={60}
                  height={20}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px solid ${theme.palette.divider}`,
            py: 2,
            px: 2,
          }}
        >
          {[...Array(4)].map((_, index) => (
            <Box
              key={index}
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                borderRight:
                  index < 3 ? `1px solid ${theme.palette.divider}` : "none",
              }}
            >
              <Skeleton variant="text" width={40} height={40} />
              <Skeleton
                variant="text"
                width={60}
                height={20}
                sx={{ mt: 0.5 }}
              />
            </Box>
          ))}
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: "4px",
          height: 420,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // boxShadow:
          //   theme.palette.mode === "dark"
          //     ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
          //     : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
        }}
      >
        <Box
          sx={{
            height: 420,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <CveChart data={vulnerabilityData} onChartClick={handleChartClick} />
        </Box>
        <DrawerWrapper
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          header={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                CVE
              </Typography>
              {/* <img
            src={Layer123}
            alt="Layer123"
            style={{ height: 22, cursor: "pointer" }}
            onClick={() => {

              navigate("/on-premises/cve");
            }}
          /> */}
              <LaunchSharpIcon
                sx={{ color: isDarkMode ? "white" : "black", cursor: "pointer" }}
                onClick={() => navigate("/on-premises/cve")}
              />
            </Box>
          }
          styles={{ width: "50%", padding: 0, margin: 0 }}
          body={
            <AgGridTable
              columnDefs={[
                {
                  field: "cve_id",
                  headerName: "CVE",
                  width: 140,
                  minWidth: 120,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  },
                  cellRenderer: (params: ICellRendererParams) => (
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkMode ? "#FFFFFF" : "#000000",
                        fontWeight: 600,
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {params.value}
                    </Typography>
                  ),
                },
                {
                  field: "score",
                  headerName: "CVSS",
                  width: 70,
                  minWidth: 60,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                  },
                  cellRenderer: (params: ICellRendererParams) => (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textAlign: "center", width: "100%" }}
                    >
                      {params.value}
                    </Typography>
                  ),
                },
                {
                  field: "severity",
                  headerName: "Severity",
                  width: 140,
                  minWidth: 100,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                  },
                  cellRenderer: (params: any) => {
                    const severityStyles: any = {
                      Critical: { bgcolor: isDarkMode ? "rgba(255, 61, 61, 0.18)" : "#67000424", color: isDarkMode ? "#FF3D3D" : "#670004" },
    High: { bgcolor: isDarkMode ? "rgba(255, 107, 107, 0.15)" : "#A7000F24", color: isDarkMode ? "#FF6B6B" : "#A7000F" },
    Medium: { bgcolor: isDarkMode ? "rgba(255, 167, 38, 0.15)" : "#E8580024", color: isDarkMode ? "#FFA726" : "#E85800" },
    Low: { bgcolor: isDarkMode ? "rgba(255, 224, 130, 0.15)" : "#F2BF0024", color: isDarkMode ? "#FFE082" : "#F2BF00" },
  };
                    return (
                      <Chip
                        label={params.value}
                        size="small"
                        sx={{
                          bgcolor: severityStyles[params.value]?.bgcolor || "#f5f5f5",
                          color: severityStyles[params.value]?.color || "#666666",
                          height: "20px",
                          borderRadius: "4px",
                          position: "relative",
                          fontWeight: 600,
                          p: 1.5,
                          pr: 0,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 8,
                            height: 8,
                            bgcolor: severityStyles[params.value]?.color || "#666666",
                            borderRadius: "50%",
                          },
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    );
                  },
                },
                {
                  field: "cisas_kev",
                  headerName: "CISA's KEV",
                  width: 120,
                  minWidth: 100,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                  },
                  headerComponent: () => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <span>CISA's KEV</span>
                      <Tooltip title="Known Exploited Vulnerabilities">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ),
                  cellRenderer: (params: ICellRendererParams) => (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: params.value
                          ? "#F53B3B"
                          : isDarkMode
                            ? "#FFFFFF"
                            : "#000000",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {params.value ? "Exploited " : "Not Exploited"}
                    </Typography>
                  ),
                },
                {
                  field: "epss",
                  headerName: "EPSS",
                  width: 90,
                  minWidth: 80,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                  },
                  headerComponent: () => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <span>EPSS</span>
                      <Tooltip title="Exploit Prediction Scoring System">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ),
                  cellRenderer: (params: ICellRendererParams) => (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textAlign: "center", width: "100%" }}
                    >
                      {(parseFloat(params.value) * 100).toFixed(2)}%
                    </Typography>
                  ),
                },
                {
                  field: "asset_count",
                  headerName: "Affected Assets",
                  width: 150,
                  minWidth: 120,
                  cellStyle: {
                    display: "flex",
                    alignItems: "center",
                  },
                  cellRenderer: (params: ICellRendererParams) => (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#DC2626",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {params.value}
                    </Typography>
                  ),
                },
              ]}
              isLoading={isLoading}
              data={tableData}
              showSearch={true}
              height={'calc(100vh - 78px)'}
              onRowClick={onRowClick}
            />
          }
        />
      </Paper>
      {selectedRow &&
        <CVEDetailsDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          cveData={{
            cve_id: selectedCVE?.cve_id,
            severity: selectedCVE?.severity,
            cvss_score: parseFloat(selectedCVE?.score),
            affected_assets: selectedCVE?.asset_count,
            summaryData: selectedCVE?.summaryData,
            cisa_key: selectedCVE?.cisas_kev,
          }}
          isLoading={isLoading}
          showBackButton={true}
        />
      }
    </>
  );
};

export default Cve;
